const SoftDeletePGAdapter = require('../../common/soft-delete-pg-adapter.js')
const { getInsertProperties } = require('../../tools/sql-tools')
const { v4 } = require('uuid')

/**
 * @typedef {Object} StockSummary
 * @property {string} location_sku
 * @property {string} yearweek
 * @property {string} sku
 * @property {string} product
 * @property {string} location
 * @property {string} date
 * @property {number} closing
 * @property {number} prev_opening
 * @property {number} sold
 * @property {number} delivered
 * @property {number} current_opening
 * @property {number} sellprice
 * @property {number} sales_value
 * @property {string} notes
 * @property {number} prev_sl_balance
 * @property {number} sl_balance
 * @property {number} sl_sold
 * @property {number} prev_partner_balance
 * @property {number} partner_balance
 * @property {number} partner_sold
 * @property {string} driver
 * @property {string} delivery_notes
 * @property {number} delivered_value
 * @property {number} invoice_adjustment
 * @property {number} forecast_adjustment
 * @property {string|null} invoice_adjustment_date
 * @property {string} transaction_id - UUID
 * @property {number} plus_vat
 * @property {string} location_id - UUID
 * @property {string} service_id
 */

class StockPGAdapter extends SoftDeletePGAdapter {
  constructor ({ pgConnection, username, logger }) {
    const columns = [
      'created_at',
      'updated_at',
      'transaction_id',
      'location_fsid_sku',
      'yearweek',
      'sku',
      'event_type',
      'event_id',
      'location_fsid',
      'date',
      'closing',
      'prev_opening',
      'sold',
      'delivered',
      'current_opening',
      'notes',
      'prev_sl_balance',
      'sl_balance',
      'sl_sold',
      'prev_partner_balance',
      'partner_balance',
      'partner_sold',
      'location_id',
      'driver',
      'delivery_notes',
      'service_id',
      'parent_event_date',
      'delivery_type',
      'data_source',
      'parent_event_invoice_merge'
    ]
    super(pgConnection, 'avocado.data_stocksummaryevent', username, columns, 'id', {}, logger)
  }

  async createStockSummaryEvents (locationId, yearweek, payloadRows) {
    this.logger.info(`upserting ${payloadRows.length} rows for location "${locationId}" and yearweek "${yearweek}"`)

    const rowsWithMissingSku = payloadRows.filter(row => !row.sku)
    if (rowsWithMissingSku.length > 0) {
      this.logger.warn(`found ${rowsWithMissingSku.length} rows with missing sku:`, rowsWithMissingSku)
      payloadRows = payloadRows.filter(row => row.sku)
    }

    // validate payload
    // look for rows with different location id
    const rowsWithDifferentLocationId = payloadRows.filter(row => row.location_id !== locationId)
    if (rowsWithDifferentLocationId.length > 0) {
      throw new Error(`found ${rowsWithDifferentLocationId.length} rows not matching the location id (${locationId}): ${rowsWithDifferentLocationId.map(row => row.location_id)}`)
    }
    // look for rows with different yearweek
    const rowsWithDifferentYearweek = payloadRows.filter(row => row.yearweek !== yearweek)
    if (rowsWithDifferentYearweek.length > 0) {
      throw new Error(`found ${rowsWithDifferentYearweek.length} rows not matching the yearweek (${yearweek}): ${rowsWithDifferentYearweek.map(row => row.yearweek)}`)
    }
    // look for duplicates in terms of location_id, sku and date
    const byConstraintCounts = payloadRows.reduce((memo, row) => {
      const constraintKey = `${row.location_id}:${row.sku}:${row.event_type}:${row.date}`
      memo[constraintKey] = memo[constraintKey] || 0
      memo[constraintKey] += 1
      return memo
    }, {})
    const duplicates = Object.keys(byConstraintCounts).filter(key => byConstraintCounts[key] > 1)
    if (duplicates.length > 0) {
      this.logger.warn(`found ${duplicates.length} duplicate rows for location ${locationId} on period ${yearweek}. Those will not be exported: ${duplicates.join(',')}:`, duplicates)
      payloadRows = payloadRows.filter(row => byConstraintCounts[`${row.location_id}:${row.sku}:${row.event_type}:${row.date}`] === 1)
    }

    // pgConnection can either be a client or a pool.
    // if it's a pool, use an own client to ensure all queries are being ran against the same client
    const useOwnClient = !('connection' in this.pgConnection)
    const client = useOwnClient ? await this.pgConnection.connect() : this.pgConnection

    try {
      await client.query('BEGIN')
      const upsertionResult = await this.upsert(payloadRows, { conflictIdColumns: ['location_id', 'sku', 'event_type', 'date'], client })
      const upsertedIds = upsertionResult.map(row => row[this.idColumn])
      const filter = {
        location_id: locationId,
        yearweek,
        excludeIds: upsertedIds
      }
      const preparers = {
        // Note we can't use `id NOT IN ($3)` here cause we need them casted as uuid
        excludeIds: (key, value) => [{
          statement: 'NOT (id = ANY(%INDEX%::UUID[]))',
          value
        }]
      }
      const deletionResult = await this.deleteWhere({ filter, preparers }, { client })
      await client.query('COMMIT')
      this.logger.info(`upserted ${upsertionResult.length} rows, deleted ${deletionResult.length} rows`)
    } catch (e) {
      await client.query('ROLLBACK')
      throw e
    } finally {
      // if we use our own client, relase it
      if (useOwnClient) {
        client.release()
      }
    }
  }

  /**
   * @param {string} formattedFsid
   * @param {string} yearweek
   * @param {StockSummary[]} payloadRows
   */
  async createStockSummary (formattedFsid, yearweek, payloadRows) {
    if (!formattedFsid || !yearweek) {
      this.logger.warn({
        code: 'WARNING: missing location or yearweek',
        formattedFsid,
        yearweek,
        status: 'Will not delete, only do upsert'
      })
    }

    const useOwnClient = !('connection' in this.pgConnection)
    const client = useOwnClient ? await this.pgConnection.connect() : this.pgConnection
    let response = []

    try {
      await client.query('BEGIN;')
      if (formattedFsid && yearweek) {
        await client.query(`
          DELETE
          FROM avocado.data_stocksummary
          where (location, yearweek) = ($1, $2);
        `, [formattedFsid, yearweek])
      }

      if (payloadRows && payloadRows.length > 0) {
        const transformedRows = payloadRows.map(r => {
          return {
            id: v4(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            ...r
          }
        })

        const { columns, values, placeholders } = getInsertProperties(transformedRows)
        const { rows } = await client.query(`
          INSERT INTO avocado.data_stocksummary (${columns})
          VALUES ${placeholders}
          ON CONFLICT (location_id, sku, date)
          DO UPDATE SET
            updated_at = excluded.updated_at,
            transaction_id = excluded.transaction_id,
            location_sku = excluded.location_sku,
            yearweek = excluded.yearweek,
            product = excluded.product,
            location = excluded.location,
            closing = excluded.closing,
            prev_opening = excluded.prev_opening,
            sold = excluded.sold,
            special = excluded.special,
            delivered = excluded.delivered,
            current_opening = excluded.current_opening,
            sellprice = excluded.sellprice,
            buyprice = excluded.buyprice,
            sales_value = excluded.sales_value,
            credit = excluded.credit,
            credit_value = excluded.credit_value,
            notes = excluded.notes,
            plus_vat = excluded.plus_vat,
            prev_sl_balance = excluded.prev_sl_balance,
            sl_balance = excluded.sl_balance,
            sl_sold = excluded.sl_sold,
            prev_partner_balance = excluded.prev_partner_balance,
            partner_balance = excluded.partner_balance,
            partner_sold = excluded.partner_sold,
            driver = excluded.driver,
            delivery_notes = excluded.delivery_notes,
            service_id = excluded.service_id,
            delivered_value = excluded.delivered_value,
            invoice_adjustment = excluded.invoice_adjustment,
            forecast_adjustment = excluded.forecast_adjustment,
            invoice_adjustment_date = excluded.invoice_adjustment_date
          RETURNING *;
        `, values)
        response = rows
      }

      await client.query('COMMIT;')
    } catch (e) {
      await client.query('ROLLBACK;')
      throw e
    } finally {
      if (useOwnClient) {
        client.release()
      }
    }
    return response
  }
}

module.exports = StockPGAdapter
